/* Estimates use the same Shopify rate service as checkout. No carrier credentials are exposed. */
(() => {
  if (customElements.get('shipping-calculator')) return;
  const events = ['on:line-item:change', 'on:cart:add', 'dispatch:cart-drawer:refresh', 'on:bfcache:load-restore'];
  const addressService = 'https://services.arcgis.com/xdsHIIxuCWByZiCB/arcgis/rest/services/LINZ_NZ_Addresses/FeatureServer/0/query';
  const roadTypes = { RD: 'ROAD', ST: 'STREET', AVE: 'AVENUE', DR: 'DRIVE', PL: 'PLACE', CRES: 'CRESCENT', CT: 'COURT', TCE: 'TERRACE', HWY: 'HIGHWAY', PDE: 'PARADE', GRV: 'GROVE', LN: 'LANE', CL: 'CLOSE' };
  let savedAddress = {};
  const signature = cart => JSON.stringify([cart.currency, cart.total_price, cart.items.map(item => [item.key, item.quantity, item.final_line_price])]);
  class ShippingCalculator extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.ready) return;
        this.ready = true;
        this.fields = [...this.querySelectorAll('[data-address-field]')];
        this.button = this.querySelector('[data-estimate-submit]');
        this.status = this.querySelector('[data-estimate-status]');
        this.rates = this.querySelector('[data-estimate-rates]');
        this.street = this.querySelector('[data-address-field="address1"]');
        this.suggestions = this.querySelector('[data-address-suggestions]');
        this.addressStatus = this.querySelector('[data-address-status]');
        this.buttonText = this.button.textContent;
        this.fields.forEach(field => { if (savedAddress[field.dataset.addressField]) field.value = savedAddress[field.dataset.addressField]; });
        this.clickHandler = () => this.calculate();
        this.inputHandler = event => {
          savedAddress = this.address(); this.invalidate();
          if (event.target === this.street && event.type === 'input' && !event.isComposing) this.queueSuggestions();
          else if (event.target !== this.street) this.closeSuggestions();
        };
        this.keyHandler = event => {
          if (event.target === this.street && this.suggestionKey(event)) return;
          if (event.key === 'Enter' && event.target.matches('input')) { event.preventDefault(); this.calculate(); }
        };
        this.suggestionClick = event => {
          const option = event.target.closest('[data-address-index]');
          if (option) this.selectAddress(Number(option.dataset.addressIndex));
        };
        this.suggestionPointer = event => event.preventDefault();
        this.suggestionBlur = event => { if (event.target === this.street) this.closeSuggestions(false); };
        this.suggestionFocus = () => this.queueSuggestions();
        this.suggestions.addEventListener('click', this.suggestionClick);
        this.suggestions.addEventListener('pointerdown', this.suggestionPointer);
        this.street.addEventListener('blur', this.suggestionBlur);
        this.street.addEventListener('focus', this.suggestionFocus);
        this.cartHandler = () => { this.invalidate(this.dataset.changed); this.refreshCart(); };
        this.button.addEventListener('click', this.clickHandler);
        this.addEventListener('input', this.inputHandler);
        this.addEventListener('change', this.inputHandler);
        this.addEventListener('keydown', this.keyHandler);
        events.forEach(name => document.addEventListener(name, this.cartHandler));
        this.pageHandler = event => { if (event.persisted) this.cartHandler(); };
        window.addEventListener('pageshow', this.pageHandler);
        this.refreshCart();
      });
    }
    disconnectedCallback() {
      this.invalidate();
      this.closeSuggestions();
      this.suggestions?.removeEventListener('click', this.suggestionClick);
      this.suggestions?.removeEventListener('pointerdown', this.suggestionPointer);
      this.street?.removeEventListener('blur', this.suggestionBlur);
      this.street?.removeEventListener('focus', this.suggestionFocus);
      this.button?.removeEventListener('click', this.clickHandler);
      this.removeEventListener('input', this.inputHandler);
      this.removeEventListener('change', this.inputHandler);
      this.removeEventListener('keydown', this.keyHandler);
      events.forEach(name => document.removeEventListener(name, this.cartHandler));
      window.removeEventListener('pageshow', this.pageHandler);
      this.ready = false;
    }
    closeSuggestions(clearMessage = true) {
      clearTimeout(this.suggestionTimer);
      this.suggestionController?.abort();
      this.suggestionRequest = (this.suggestionRequest || 0) + 1;
      this.addressMatches = [];
      this.activeSuggestion = -1;
      if (!this.suggestions) return;
      this.suggestions.replaceChildren();
      this.suggestions.hidden = true;
      this.street.setAttribute('aria-expanded', 'false');
      this.street.removeAttribute('aria-activedescendant');
      if (clearMessage) this.addressStatus.textContent = '';
    }
    queueSuggestions() {
      this.closeSuggestions();
      const typed = this.street.value.trim().replace(/\s+/g, ' ');
      // Require a street number and enough letters; SQL wildcards are never accepted.
      if (typed.length < 5 || !/^\d[\p{L}\p{M}\d\s.,'’()/\-]*$/u.test(typed) || !/\p{L}{2}/u.test(typed)) return;
      const query = typed.normalize('NFD').replace(/\p{M}/gu, '').replace(/’/g, "'")
        .toUpperCase().replace(/\s*\/\s*/g, '/').replace(/\s*,\s*/g, ', ')
        .replace(/\b(RD|ST|AVE|DR|PL|CRES|CT|TCE|HWY|PDE|GRV|LN|CL)\.?(?=,|$)/g, (_, value) => roadTypes[value]);
      const requestId = this.suggestionRequest;
      this.suggestionTimer = setTimeout(() => this.findAddresses(query, requestId), 350);
    }
    async findAddresses(query, requestId) {
      if (requestId !== this.suggestionRequest || !this.isConnected) return;
      const controller = new AbortController();
      this.suggestionController = controller;
      const timeout = setTimeout(() => controller.abort(), 6000);
      this.addressStatus.textContent = this.dataset.addressSearching;
      const params = new URLSearchParams({ f: 'json', returnGeometry: 'false', resultRecordCount: '6',
        outFields: 'address_id,full_address,full_address_number,full_road_name,suburb_locality,town_city',
        where: `address_lifecycle='Current' AND UPPER(full_address_ascii) LIKE '${query.replace(/'/g, "''")}%'` });
      try {
        const response = await fetch(`${addressService}?${params}`, { signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
        if (!response.ok) throw new Error('address_unavailable');
        const result = await response.json();
        if (result.error || !Array.isArray(result.features)) throw new Error('address_unavailable');
        if (requestId !== this.suggestionRequest || !this.isConnected) return;
        const unique = new Map();
        for (const feature of result.features.slice(0, 6)) {
          const address = feature.attributes || {};
          const street = `${address.full_address_number || ''} ${address.full_road_name || ''}`.trim();
          const label = address.full_address;
          if (typeof label !== 'string' || !label || label.length > 320 || !address.full_address_number || !address.full_road_name || street.length > 160) continue;
          unique.set(label, { label, address1: street, address2: String(address.suburb_locality || '').slice(0, 80), city: String(address.town_city || '').slice(0, 80) });
        }
        this.addressMatches = [...unique.values()];
        this.suggestions.replaceChildren(...this.addressMatches.map((address, index) => {
          const li = document.createElement('li');
          li.id = `${this.suggestions.id}-${index}`;
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          li.dataset.addressIndex = index;
          li.textContent = address.label;
          return li;
        }));
        const found = this.addressMatches.length > 0;
        this.suggestions.hidden = !found;
        this.street.setAttribute('aria-expanded', String(found));
        this.addressStatus.textContent = found ? this.dataset.addressResults : this.dataset.addressNone;
      } catch {
        if (requestId === this.suggestionRequest && this.isConnected) this.addressStatus.textContent = this.dataset.addressUnavailable;
      } finally { clearTimeout(timeout); }
    }
    suggestionKey(event) {
      if (event.key === 'Escape' && !this.suggestions.hidden) {
        event.preventDefault(); event.stopPropagation(); this.closeSuggestions(); return true;
      }
      if (this.suggestions.hidden || !this.addressMatches.length) return false;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const count = this.addressMatches.length;
        this.activeSuggestion = this.activeSuggestion < 0 ? (event.key === 'ArrowDown' ? 0 : count - 1)
          : (this.activeSuggestion + (event.key === 'ArrowDown' ? 1 : -1) + count) % count;
        [...this.suggestions.children].forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeSuggestion)));
        const active = this.suggestions.children[this.activeSuggestion];
        this.street.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
        return true;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (this.activeSuggestion >= 0) this.selectAddress(this.activeSuggestion);
        return true;
      }
      return false;
    }
    selectAddress(index) {
      const selected = this.addressMatches[index];
      if (!selected) return;
      // LINZ supplies physical localities, not postal codes or regional authorities.
      // Clear previous postal details so a different address cannot inherit them.
      for (const field of this.fields) field.value = selected[field.dataset.addressField] || '';
      this.closeSuggestions();
      savedAddress = this.address();
      this.invalidate();
      this.addressStatus.textContent = this.dataset.addressSelected;
      this.querySelector('[data-address-field="province"]').focus();
    }
    address() { return Object.fromEntries(this.fields.map(field => [field.dataset.addressField, field.value.trim()])); }
    busy(value) {
      this.button.disabled = value;
      this.button.textContent = value ? this.dataset.loading : this.buttonText;
      this.setAttribute('aria-busy', String(value));
    }
    invalidate(message = '') {
      this.controller?.abort();
      this.requestId = (this.requestId || 0) + 1;
      if (!this.button) return;
      this.busy(false);
      this.rates.replaceChildren();
      this.rates.hidden = true;
      this.status.textContent = message;
    }
    async json(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
      if (!response.ok) throw new Error('request_failed');
      return response.json();
    }
    updateSummary(cart) {
      const list = this.querySelector('[data-estimate-cart]');
      if (!list) return;
      list.replaceChildren(...cart.items.map(item => {
        const li = document.createElement('li');
        li.textContent = `${item.quantity} × ${item.product_title || item.title}${item.variant_title && item.variant_title !== 'Default Title' ? ` — ${item.variant_title}` : ''}`;
        return li;
      }));
      this.querySelector('[data-estimate-empty]').hidden = cart.item_count > 0;
    }
    async refreshCart() {
      if (!this.querySelector('[data-estimate-cart]')) return;
      try { this.updateSummary(await this.json(`${this.dataset.cartUrl}.js`)); } catch { /* Calculate provides the error and retry path. */ }
    }
    async calculate() {
      if (this.button.disabled) return;
      this.closeSuggestions();
      this.invalidate();
      for (const field of this.fields) { field.value = field.value.trim(); if (!field.reportValidity()) return; }
      savedAddress = this.address();
      const requestId = this.requestId;
      const controller = new AbortController();
      this.controller = controller;
      const { signal } = controller;
      const timeout = setTimeout(() => controller.abort(), 30000);
      this.busy(true);
      this.status.textContent = this.dataset.loading;
      try {
        const cart = await this.json(`${this.dataset.cartUrl}.js`, { signal });
        this.updateSummary(cart);
        if (!cart.item_count) { this.status.textContent = this.dataset.empty; return; }
        if (!cart.requires_shipping) { this.status.textContent = this.dataset.noShipping; return; }
        const params = new URLSearchParams();
        Object.entries({ ...savedAddress, country: 'New Zealand' }).forEach(([key, value]) => params.set(`shipping_address[${key}]`, value));
        const base = this.dataset.cartUrl;
        await this.json(`${base}/prepare_shipping_rates.json?${params}`, { method: 'POST', signal });
        let result;
        for (let attempt = 0; attempt < 24; attempt += 1) {
          if (signal.aborted) throw new Error('cancelled');
          result = await this.json(`${base}/async_shipping_rates.json?${params}`, { signal });
          if (result !== null) break;
          await new Promise(resolve => setTimeout(resolve, 750));
        }
        if (!result || !Array.isArray(result.shipping_rates)) throw new Error('rates_not_ready');
        const freshCart = await this.json(`${base}.js`, { signal });
        if (requestId !== this.requestId || !this.isConnected) return;
        if (signature(cart) !== signature(freshCart)) { this.updateSummary(freshCart); this.status.textContent = this.dataset.changed; return; }
        if (!result.shipping_rates.length) { this.status.textContent = this.dataset.noRates; return; }
        const currency = cart.currency || this.dataset.currency || 'NZD';
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', { style: 'currency', currency, currencyDisplay: 'code' });
        for (const rate of result.shipping_rates) {
          const amount = Number(rate.price);
          if (!Number.isFinite(amount) || amount < 0) throw new Error('invalid_price');
          const li = document.createElement('li');
          const name = document.createElement('strong');
          const price = document.createElement('span');
          name.textContent = rate.presentment_name || rate.name;
          price.textContent = money.format(amount);
          li.append(name, price);
          if (rate.description) { const detail = document.createElement('small'); detail.textContent = rate.description; li.append(detail); }
          this.rates.append(li);
        }
        this.rates.hidden = false;
        this.status.textContent = '';
      } catch {
        if (requestId === this.requestId && this.isConnected) { this.rates.replaceChildren(); this.rates.hidden = true; this.status.textContent = this.dataset.error; }
      } finally {
        clearTimeout(timeout);
        if (requestId === this.requestId && this.isConnected) this.busy(false);
      }
    }
  }
  customElements.define('shipping-calculator', ShippingCalculator);
})();
